import Link from "next/link"
import { Clock3, Sparkles } from "lucide-react"

import type { BlogArticle } from "@/types/blog"
import { formatDate } from "@/lib/utils"

type BlogCardProps = {
  article: BlogArticle
  featured?: boolean
}

const categoryMap: Record<BlogArticle["category"], string> = {
  guides: "Гайди",
  safety: "Безпека",
  seasonal: "Сезонне",
  fermentation: "Ферментація",
  healthy: "Корисне",
  storage: "Зберігання",
  tools: "Інструменти",
  other: "Статті",
}

export function BlogCard({ article, featured = false }: BlogCardProps) {
  return (
    <article className="group overflow-hidden rounded-[1.75rem] border border-neutral-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
      <Link href={`/blog/${article.slug}`} className="block">
        <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-emerald-100 via-amber-50 to-white">
          <div className="absolute left-4 top-4 inline-flex rounded-full border border-white/80 bg-white/90 px-3 py-1 text-xs font-semibold text-neutral-800 backdrop-blur">
            {categoryMap[article.category]}
          </div>

          {featured ? (
            <div className="absolute right-4 top-4 inline-flex items-center rounded-full border border-amber-200 bg-amber-50/95 px-3 py-1 text-xs font-semibold text-amber-800 backdrop-blur">
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              Рекомендація
            </div>
          ) : null}

          <div className="absolute inset-0 flex items-center justify-center text-6xl opacity-90">
            📝
          </div>
        </div>
      </Link>

      <div className="p-5">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-xs font-medium text-neutral-700">
            <Clock3 className="mr-1.5 h-3.5 w-3.5" />
            {article.readingTimeMinutes} хв
          </span>

          <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800">
            {formatDate(article.updatedAt ?? article.publishedAt)}
          </span>
        </div>

        <Link href={`/blog/${article.slug}`} className="block">
          <h3 className="line-clamp-2 text-xl font-semibold leading-snug text-neutral-950 transition-colors group-hover:text-emerald-700">
            {article.title}
          </h3>
        </Link>

        <p className="mt-3 line-clamp-3 text-sm leading-7 text-neutral-600">
          {article.excerpt}
        </p>

        <div className="mt-5 inline-flex items-center text-sm font-semibold text-emerald-700 transition-colors hover:text-emerald-800">
          Читати статтю
          <span className="ml-2">→</span>
        </div>
      </div>
    </article>
  )
}